import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import { postService } from '@/services/data';
import { logger } from '@/utils/logger';

export interface UsePostOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

export const usePost = (postId: string | undefined, options?: UsePostOptions) => {
  const { user, loading: authLoading } = useAuth();

  const {
    data: post,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['post', postId, user?.id],
    queryFn: async () => {
      if (!postId || !user?.id) return null;

      logger.debug('🔍 usePost fetching post:', postId);

      const response = await postService.getPost(postId);

      if (response.error) {
        logger.error('❌ Error fetching post:', response.error);
        throw new Error(response.error.message);
      }

      logger.debug('✅ Post fetched:', response.data?.id);
      return response.data;
    },
    enabled: !!postId && !!user?.id && !authLoading && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    post,
    isLoading,
    isFetching,
    error: error?.message || null,
    refetch,
  };
};
